import React, { useEffect, useState } from "react";

import {
  Check,
  ChevronRight,
  Globe,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Palette,
  Sun,
  SunMoon,
  UserCircle2,
} from "lucide-react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/auth-context";
import { useTheme } from "../../contexts/theme-context";
import { useTranslations } from "../../hooks/use-translations";
import { apiClient } from "../../lib/api";
import { getUserPreferences, setUserPreferences } from "../../lib/user-preferences";

export const Header = (): React.JSX.Element => {
  const { user, isAuthenticated, logout } = useAuth();
  const { translations } = useTranslations();
  const { theme, setTheme } = useTheme();
  const pref = getUserPreferences();
  const [open, setOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<null | `theme` | `language`>(null);
  const [language, setLanguage] = useState(pref.language);

  const closePanel = () => setOpen(false);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === `Escape`) {
        closePanel();
      }
    }

    if (open) {
      window.addEventListener(`keydown`, onKey);
    }

    return () => {
      window.removeEventListener(`keydown`, onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    logout();
  };

  function applyTheme(next: `auto` | `dark` | `light`) {
    const root = window.document.documentElement;

    if (next === `dark`) {
      root.classList.add(`dark`);
    } else if (next === `light`) {
      root.classList.remove(`dark`);
    } else {
      root.classList.remove(`dark`);

      if (window.matchMedia(`(prefers-color-scheme: dark)`).matches) {
        root.classList.add(`dark`);
      }
    }

    setTheme(next);

    if (isAuthenticated) {
      apiClient.updatePreferences({ theme: next });
    }
  }

  function applyLanguage(next: `auto` | `en` | `es` | `pt`) {
    setLanguage(next);

    setUserPreferences({ language: next });

    if (isAuthenticated) {
      apiClient.updatePreferences({ language: next });
    }

    window.dispatchEvent(new Event(`languagechange`));
  }

  const overlay = open
    ? createPortal(
        <div aria-modal="true" className="fixed inset-0 z-[55]" role="dialog">
          {/* Backdrop */}
          <button
            aria-label="close profile panel"
            className="absolute inset-0 w-full h-full bg-black/40 backdrop-blur-sm cursor-pointer"
            onClick={closePanel}
            type="button"
          />
          {/* Panel: full-width on phones, constrained sidebar from small breakpoint upward */}
          <div className="absolute top-16 right-0 h-[calc(100%-4rem)] w-full sm:w-80 bg-white dark:bg-gray-900 border-l dark:border-gray-800 shadow-xl flex flex-col overflow-y-auto p-0 text-left select-none">
            {isAuthenticated && user ? (
              <React.Fragment>
                <div className="px-5 pt-6 pb-4 border-b bg-white dark:bg-gray-800 dark:border-gray-800">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                      <span className="text-lg font-semibold text-gray-600 dark:text-gray-300">
                        {(user.firstName?.[0] ?? ``).toUpperCase()}
                        {(user.lastName?.[0] ?? ``).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {user.firstName ?? ``} {user.lastName ?? ``}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 truncate">{user.email}</div>
                    </div>
                  </div>
                </div>
                <nav className="flex-1 py-2 text-[13px] relative">
                  <ul>
                    <li>
                      <button
                        aria-expanded={openSubmenu === `theme`}
                        aria-haspopup="true"
                        className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#316ac5] hover:text-white text-gray-800 dark:text-gray-100 dark:hover:bg-primary-700"
                        onClick={() => setOpenSubmenu(openSubmenu === `theme` ? null : `theme`)}
                        type="button"
                      >
                        <span className="flex items-center gap-2">
                          <Palette className="h-4 w-4" />
                          {translations.header.theme}
                        </span>
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${openSubmenu === `theme` ? `rotate-90` : ``}`}
                        />
                      </button>
                      {openSubmenu === `theme` ? (
                        <div className="mt-1 pl-8 pr-2 space-y-1">
                          {([`auto`, `dark`, `light`] as Array<`auto` | `dark` | `light`>).map((opt) => (
                            <button
                              key={opt}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-[#316ac5] hover:text-white dark:hover:bg-primary-700 ${theme === opt ? `bg-[#3875d7] text-white dark:bg-primary-600` : `text-gray-800 dark:text-gray-100`}`}
                              onClick={() => applyTheme(opt)}
                              type="button"
                            >
                              {opt === `auto` ? <SunMoon className="h-4 w-4" /> : null}
                              {opt === `dark` ? <Moon className="h-4 w-4" /> : null}
                              {opt === `light` ? <Sun className="h-4 w-4" /> : null}
                              <span className="flex-1 capitalize">{opt}</span>
                              {theme === opt ? <Check className="h-4 w-4" /> : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </li>
                    <li>
                      <button
                        aria-expanded={openSubmenu === `language`}
                        aria-haspopup="true"
                        className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#316ac5] hover:text-white text-gray-800 dark:text-gray-100 dark:hover:bg-primary-700"
                        onClick={() => setOpenSubmenu(openSubmenu === `language` ? null : `language`)}
                        type="button"
                      >
                        <span className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          {translations.header.language}
                        </span>
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${openSubmenu === `language` ? `rotate-90` : ``}`}
                        />
                      </button>
                      {openSubmenu === `language` ? (
                        <div className="mt-1 pl-8 pr-2 space-y-1">
                          {([`auto`, `en`, `es`, `pt`] as Array<`auto` | `en` | `es` | `pt`>).map((opt) => (
                            <button
                              key={opt}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-[#316ac5] hover:text-white dark:hover:bg-primary-700 ${language === opt ? `bg-[#3875d7] text-white dark:bg-primary-600` : `text-gray-800 dark:text-gray-100`}`}
                              onClick={() => applyLanguage(opt)}
                              type="button"
                            >
                              <span className="flex-1 capitalize">
                                {opt === `auto` ? `Auto` : null}
                                {opt === `en` ? `English` : null}
                                {opt === `es` ? `Español` : null}
                                {opt === `pt` ? `Português` : null}
                              </span>
                              {language === opt ? <Check className="h-4 w-4" /> : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </li>
                    <li className="my-2 border-t border-gray-800 dark:border-gray-800 dark:border-b" />
                    <li>
                      <Link
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[#316ac5] hover:text-white text-gray-800 dark:text-gray-100 dark:hover:bg-primary-700"
                        onClick={closePanel}
                        to="/account/profile"
                      >
                        <UserCircle2 className="h-4 w-4" />
                        {translations.header.editProfile}
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[#316ac5] hover:text-white text-gray-800 dark:text-gray-100 dark:hover:bg-primary-700"
                        onClick={closePanel}
                        to="/account/change-password"
                      >
                        <KeyRound className="h-4 w-4" />
                        {translations.header.changePassword}
                      </Link>
                    </li>
                  </ul>
                </nav>
                <div className="mt-auto border-t dark:border-gray-800">
                  <button
                    className="w-full px-4 py-2 text-sm font-medium bg-[#e04343] text-white hover:bg-[#c63535] dark:bg-red-700 dark:hover:bg-red-600 flex items-center justify-center gap-2"
                    data-testid="logout-button"
                    type="button"
                    onClick={() => {
                      closePanel();
                      handleLogout();
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{translations.header.logout}</span>
                  </button>
                </div>
              </React.Fragment>
            ) : (
              <nav className="flex-1 py-2 text-[13px] relative">
                <ul>
                  <li>
                    <button
                      aria-expanded={openSubmenu === `theme`}
                      aria-haspopup="true"
                      className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#316ac5] hover:text-white text-gray-800 dark:text-gray-100 dark:hover:bg-primary-700"
                      onClick={() => setOpenSubmenu(openSubmenu === `theme` ? null : `theme`)}
                      type="button"
                    >
                      <span className="flex items-center gap-2">
                        <Palette className="h-4 w-4" />
                        {translations.header.theme}
                      </span>
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${openSubmenu === `theme` ? `rotate-90` : ``}`}
                      />
                    </button>
                    {openSubmenu === `theme` ? (
                      <div className="mt-1 pl-8 pr-2 space-y-1">
                        {([`auto`, `dark`, `light`] as Array<`auto` | `dark` | `light`>).map((opt) => (
                          <button
                            key={opt}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-[#316ac5] hover:text-white dark:hover:bg-primary-700 ${theme === opt ? `bg-[#3875d7] text-white dark:bg-primary-600` : `text-gray-800 dark:text-gray-100`}`}
                            onClick={() => applyTheme(opt)}
                            type="button"
                          >
                            {opt === `auto` ? <SunMoon className="h-4 w-4" /> : null}
                            {opt === `dark` ? <Moon className="h-4 w-4" /> : null}
                            {opt === `light` ? <Sun className="h-4 w-4" /> : null}
                            <span className="flex-1 capitalize">{opt}</span>
                            {theme === opt ? <Check className="h-4 w-4" /> : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </li>
                  <li>
                    <button
                      aria-expanded={openSubmenu === `language`}
                      aria-haspopup="true"
                      className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#316ac5] hover:text-white text-gray-800 dark:text-gray-100 dark:hover:bg-primary-700"
                      onClick={() => setOpenSubmenu(openSubmenu === `language` ? null : `language`)}
                      type="button"
                    >
                      <span className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        {translations.header.language}
                      </span>
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${openSubmenu === `language` ? `rotate-90` : ``}`}
                      />
                    </button>
                    {openSubmenu === `language` ? (
                      <div className="mt-1 pl-8 pr-2 space-y-1">
                        {([`auto`, `en`, `es`, `pt`] as Array<`auto` | `en` | `es` | `pt`>).map((opt) => (
                          <button
                            key={opt}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-[#316ac5] hover:text-white dark:hover:bg-primary-700 ${language === opt ? `bg-[#3875d7] text-white dark:bg-primary-600` : `text-gray-800 dark:text-gray-100`}`}
                            onClick={() => applyLanguage(opt)}
                            type="button"
                          >
                            <span className="flex-1 capitalize">
                              {opt === `auto` ? `Auto` : null}
                              {opt === `en` ? `English` : null}
                              {opt === `es` ? `Español` : null}
                              {opt === `pt` ? `Português` : null}
                            </span>
                            {language === opt ? <Check className="h-4 w-4" /> : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <React.Fragment>
      <header className="sticky top-0 z-[60] w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:bg-gray-900/80">
        <div className="flex h-16 w-full items-center justify-between px-4">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400">{translations.common.appTitle}</h1>
          </div>
          <div className="flex items-center space-x-4">
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  aria-label="profile menu"
                  className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center cursor-pointer"
                  onClick={() => setOpen(!open)}
                  type="button"
                >
                  <svg
                    className="w-5 h-5 text-gray-600 dark:text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.25a8.25 8.25 0 1 1 15 0v.75H4.5v-.75Z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="relative">
                <button
                  aria-label="menu"
                  className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-600 dark:text-gray-300"
                  onClick={() => setOpen(!open)}
                  type="button"
                >
                  <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {overlay}
    </React.Fragment>
  );
};
